import { useState } from 'react';

const STORAGE_KEY = 'bi_disclosure_accepted';

const DISCLOSURE_TEXT = {
  title: 'Important Notice — Please Read Before Proceeding',
  paragraphs: [
    'Bearing Intelligence provides open-source geopolitical analysis derived exclusively from publicly available information including commercial satellite imagery, regional press reporting, think tank assessments, and official foreign government statements.',
    'This platform is provided for informational and educational purposes only. All analysis represents the editorial judgment of Bearing Intelligence and does not represent the position of any government, military, or intelligence agency. No content on this platform is or can be classified.',
    'Bearing Intelligence does not assess, estimate, or report on US or allied nation force composition, disposition, or strength. Adversary and neutral force estimates, where included, are derived solely from open-source reporting and carry explicit confidence intervals reflecting source quality and veracity.',
    'Nothing on this platform constitutes legal, financial, military, or operational advice. Bearing Intelligence assumes no liability for decisions made based on content provided herein. Users are solely responsible for how they interpret and apply this information.',
    'This is a beta platform. Content accuracy, availability, and completeness are not guaranteed. By continuing, you acknowledge these limitations and agree to use this platform responsibly and in accordance with all applicable laws and regulations.',
  ],
  acknowledge: 'I understand and acknowledge that this platform provides open-source analysis for informational purposes only, that no content is classified or represents any government position, and that I assume full responsibility for my use of this information.',
  button: 'I Acknowledge and Accept',
};

export function useDisclosure() {
  const [accepted, setAccepted] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setAccepted(true);
  };

  return { accepted, accept };
}

export function DisclosureModal({ onAccept }) {
  const [checked, setChecked] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const contentRef = (el) => {
    if (el && el.scrollHeight <= el.clientHeight + 40) {
      setScrolled(true);
    }
  };

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 40) {
      setScrolled(true);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      backgroundColor: 'rgba(0,0,0,0.92)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(231,76,60,0.4)',
        borderRadius: 12,
        maxWidth: 640, width: '100%',
        maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(231,76,60,0.15)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <img src="/production-logo.svg" alt="Bearing Intelligence" style={{ height: 28, filter: 'brightness(0)' }} />
            <span style={{ color: '#E74C3C', fontFamily: 'Merriweather, Georgia, serif', fontSize: 18, fontWeight: 'bold' }}>
              Bearing Intelligence
            </span>
          </div>
          <div style={{ color: '#2C3E50', fontSize: 14, fontWeight: 'bold', letterSpacing: '0.3px' }}>
            {DISCLOSURE_TEXT.title}
          </div>
        </div>

        {/* Scrollable content */}
        <div ref={contentRef} onScroll={handleScroll} style={{
          flex: 1, overflowY: 'auto', padding: '20px 24px',
          scrollbarWidth: 'thin',
        }}>
          {DISCLOSURE_TEXT.paragraphs.map((p, i) => (
            <p key={i} style={{
              color: '#888888', fontSize: 13, lineHeight: 1.8,
              marginBottom: 14, marginTop: 0,
              borderLeft: i === 0 ? '2px solid rgba(231,76,60,0.3)' : 'none',
              paddingLeft: i === 0 ? 12 : 0,
            }}>
              {p}
            </p>
          ))}
          <div style={{ height: 1, backgroundColor: 'rgba(231,76,60,0.15)', margin: '16px 0' }} />
          <p style={{ color: '#E74C3C', fontSize: 12, lineHeight: 1.8, fontStyle: 'italic' }}>
            {DISCLOSURE_TEXT.acknowledge}
          </p>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px 20px', borderTop: '1px solid rgba(231,76,60,0.15)', flexShrink: 0 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
              style={{ marginTop: 2, flexShrink: 0, accentColor: '#E74C3C' }} />
            <span style={{ color: '#888888', fontSize: 12, lineHeight: 1.6 }}>
              I have read and understand the above disclosure. I am 18 years of age or older and agree to use this platform for lawful, informational purposes only.
            </span>
          </label>
          <button
            onClick={() => { if (checked && scrolled) onAccept(); }}
            disabled={!checked || !scrolled}
            style={{
              width: '100%', padding: '11px 0',
              backgroundColor: checked && scrolled ? 'rgba(231,76,60,0.15)' : 'rgba(52,73,94,0.03)',
              border: `1px solid ${checked && scrolled ? '#E74C3C' : 'rgba(52,73,94,0.1)'}`,
              color: checked && scrolled ? '#E74C3C' : '#444',
              borderRadius: 6, cursor: checked && scrolled ? 'pointer' : 'not-allowed',
              fontSize: 13, fontWeight: 'bold', letterSpacing: '0.4px',
              transition: 'all 0.2s',
            }}>
            {!scrolled ? 'Please scroll to read the full disclosure' : !checked ? 'Please check the box above to continue' : DISCLOSURE_TEXT.button}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DisclosurePersistentBanner() {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="bi-notice" style={{
      backgroundColor: 'rgba(10,13,20,0.95)',
      borderBottom: '1px solid rgba(231,76,60,0.15)',
      fontSize: 11, color: '#B4BAC3',
      fontFamily: 'Georgia, serif',
    }}>
      <div style={{
        maxWidth: 1400, margin: '0 auto',
        padding: '5px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
        flexWrap: 'wrap',
      }}>
        <span style={{ color: '#E74C3C', fontWeight: 'bold', flexShrink: 0 }}>⚠ NOTICE</span>
        <span className={collapsed ? 'bi-notice-text bi-notice-text--clamp' : 'bi-notice-text'} style={{ flex: 1, minWidth: 0 }}>
          Open-source analysis for informational purposes only. Not classified. Not government-affiliated. No allied force assessments.
          {!collapsed && (
            <span> Adversary estimates carry explicit confidence intervals. No liability assumed for use of this information. Beta platform — accuracy not guaranteed.</span>
          )}
        </span>
        <button onClick={() => setCollapsed(c => !c)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand disclosure notice' : 'Collapse disclosure notice'}
          style={{
          background: 'none', border: 'none', color: '#8A93A0', cursor: 'pointer',
          fontSize: 10, flexShrink: 0, padding: '0 10px', minHeight: 44, minWidth: 44,
        }}>
          {collapsed ? 'more ▼' : 'less ▲'}
        </button>
      </div>
    </div>
  );
}
